import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useMemo, useState } from 'react';
import Select from '@jetbrains/ring-ui/components/select/select';
import classNames from 'classnames';
import InlineLoader from '@jetbrains/ring-ui/components/loader-inline/loader-inline';
import { apiPluginRuleActionValues } from '@api/@types';
import { LegacyFeatureType } from '@modules/ide-provisioner/plugins-rules/plugin-rules-bundle-dialog/legacy-feature-type';
import { assignTestId } from '@common/infra/tests/assign-test-id';
import { useFeatureFlag, useServerConfigContext } from '@common/server-config-context/server-config-context';
import { usePluginChannels } from '@common/queries/plugins';
import { ActionSelector } from '../../common/action-selector/action-selector';
import { VersionSelector } from './version-selector/version-selector';
import { ChannelSelector } from './channel-selector/channel-selector';
import styles from './internal-repo-plugin-content.css';
export function InternalRepoPluginContent(props) {
    const { className, pluginRuleType, rule, onRuleChange, legacyFeatureType } = props;
    const { data: channels, isFetching: isChannelsFetching } = usePluginChannels(pluginRuleType.pluginDefinition.id);
    const serverConfig = useServerConfigContext();
    const cloudModeEnabled = serverConfig.isCloud();
    const allowDenyPluginsListEnabled = useFeatureFlag('allowDenyPluginsListEnabled');
    const { version } = pluginRuleType;
    const [selector, setSelector] = useState(version ? 'version' : 'channel');
    const isActionAllowed = useCallback((action, version) => {
        if (!allowDenyPluginsListEnabled) {
            if (legacyFeatureType === LegacyFeatureType.DisabledPlugins) {
                switch (action) {
                    case 'force-restrict':
                    case 'disable':
                        return true;
                    default:
                        return false;
                }
            }
            if (legacyFeatureType === LegacyFeatureType.AutoInstallPlugins) {
                switch (action) {
                    case 'force-auto-install':
                    case 'auto-install':
                        return true;
                    default:
                        return false;
                }
            }
        }
        switch (action) {
            case 'auto-install':
            case 'force-auto-install':
                return !cloudModeEnabled && (version === null || version === void 0 ? void 0 : version.type) !== 'all';
            case 'allow':
            case 'disable':
            case 'force-restrict':
            case 'restrict':
                return (version === null || version === void 0 ? void 0 : version.type) !== 'latest';
            default:
                if (action === undefined) {
                    return false;
                }
                return assert_exhaustive(action);
        }
    }, [cloudModeEnabled, allowDenyPluginsListEnabled, legacyFeatureType]);
    const allowedActions = useMemo(() => apiPluginRuleActionValues.reduce((acc, action) => {
        acc[action] = isActionAllowed(action, version);
        return acc;
    }, {}), [isActionAllowed, version]);
    const defaultVersion = useMemo(() => (allowDenyPluginsListEnabled ? { type: 'all' } : { type: 'latest' }), [allowDenyPluginsListEnabled]);
    const handleSelectChange = useCallback((option) => {
        if (option && option.selector !== selector) {
            setSelector(option.selector);
            onRuleChange({
                ...rule,
                singlePluginTypeRule: {
                    ...pluginRuleType,
                    version: option.selector === 'version' ? defaultVersion : undefined,
                    channel: option.selector === 'channel' && channels
                        ? lookupStableChanelOrFirst(channels === null || channels === void 0 ? void 0 : channels.items)
                        : undefined
                },
                action: isActionAllowed(rule.action, defaultVersion) ? rule.action : undefined
            });
        }
    }, [selector, onRuleChange, rule, pluginRuleType, isActionAllowed, defaultVersion, channels]);
    const handleVersionChange = useCallback((version) => {
        onRuleChange({
            ...rule,
            singlePluginTypeRule: { ...pluginRuleType, version, channel: undefined },
            action: isActionAllowed(rule.action, version) ? rule.action : undefined
        });
    }, [isActionAllowed, onRuleChange, rule, pluginRuleType]);
    const handleChannelChange = useCallback((channel) => onRuleChange({
        ...rule,
        singlePluginTypeRule: { ...pluginRuleType, channel, version: undefined }
    }), [onRuleChange, rule, pluginRuleType]);
    const handleActionChange = useCallback((action) => onRuleChange({ ...rule, action }), [onRuleChange, rule]);
    return (_jsxs("div", { className: classNames(styles.wrapper, className), children: [isChannelsFetching && _jsx(InlineLoader, {}), !isChannelsFetching && (_jsxs("div", { className: styles.selectorsWrapper, ...assignTestId('channel-or-version-selector-wrapper'), children: [(allowDenyPluginsListEnabled ||
                        legacyFeatureType === LegacyFeatureType.AutoInstallPlugins) && (_jsx(Select, { selected: selectOptions.find(option => option.selector === selector), data: selectOptions, onChange: handleSelectChange, className: styles.channelOrVersion, ...assignTestId('channel-or-version-selector') })), (allowDenyPluginsListEnabled ||
                        legacyFeatureType === LegacyFeatureType.AutoInstallPlugins) &&
                        selector === 'version' && (_jsx(VersionSelector, { pluginId: pluginRuleType.pluginDefinition.id, version: pluginRuleType === null || pluginRuleType === void 0 ? void 0 : pluginRuleType.version, onVersionChange: handleVersionChange, className: styles.versionSelector })), (allowDenyPluginsListEnabled ||
                        legacyFeatureType === LegacyFeatureType.AutoInstallPlugins) &&
                        selector === 'channel' && (_jsx(ChannelSelector, { pluginId: pluginRuleType.pluginDefinition.id, channel: pluginRuleType === null || pluginRuleType === void 0 ? void 0 : pluginRuleType.channel, onChannelChange: handleChannelChange, className: styles.channelSelector }))] })), _jsx(ActionSelector, { className: styles.actionSelector, action: rule.action, onActionChange: handleActionChange, allowedActions: allowedActions })] }));
}
const selectOptions = [
    { label: 'Version', key: 'version', selector: 'version', ...assignTestId('version') },
    { label: 'Channel', key: 'channel', selector: 'channel', ...assignTestId('channel') }
];
function lookupStableChanelOrFirst(channels) {
    const stableChannel = channels.find(channel => channel.name === 'Stable');
    if (stableChannel) {
        return stableChannel;
    }
    return channels[0];
}
